import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { ComponentFormField } from 'bom/components';
import { ComponentType } from 'bom/enums';
import { IComponent } from 'bom/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { ManufacturerFormField } from 'parts/elements';
import { useGetUoms } from 'parts/hooks';
import { IUom } from 'parts/types';

import { IUpsertBomComponentFormData } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    reset: UseFormReset<IUpsertBomComponentFormData>
  ) => (data: IUpsertBomComponentFormData) => void;
}

export const UpsertComponentFormFields = ({ isOpen, onClose, onSubmit }: IProps) => {
  const { t } = useTranslation();

  const { setValue } = useFormContext();

  const { user } = useAuthUser();
  const { records: uoms } = useGetUoms({ filters: { isBase: 1 } }, isOpen);

  const uomOptions = useMemo(
    () => uoms.map((uom: IUom) => ({ id: uom.name, name: uom.name })),
    [uoms]
  );

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ user }),
    [user]
  );

  const onChangeComponent = useCallback(
    (component: IComponent | null) => {
      if (!component) {
        return;
      }

      if (component.manufacturerId) {
        setValue('manufacturer', {
          id: component.manufacturerId,
          name: component.manufacturerName,
        });
      }
    },
    [setValue]
  );

  return (
    <Form isOpen={isOpen} onClose={onClose} title={t('titleAddComponent')} onSubmit={onSubmit}>
      <ComponentFormField
        name="component"
        requestPayload={{ filters: { type: ComponentType.PART } }}
        onChange={onChangeComponent}
        required
      />

      <ManufacturerFormField
        name="manufacturer"
        hasCreateOption={false}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      <FormField
        name="quantity"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: t('labelQuantity'),
          placeholder: t('placeholderQuantity'),
          slotProps: {
            input: {
              min: 0,
            },
          },
          required: true,
        }}
      />

      <FormField
        name="uom"
        type={InputType.SELECT}
        inputProps={{
          label: t('labelUom'),
          placeholder: t('placeholderUom'),
          getLabel: (option: IUom) => option?.name,
          getValue: (option: IUom) => option?.name,
          options: uomOptions,
          required: true,
        }}
      />
    </Form>
  );
};
